import { API_URL } from "config";
import { MAPBOX_TOKEN } from "config";
import { useAuth } from "contexts/AuthContext";
import FeatureService from "../utils/featureService";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Button } from "@mui/material";

import { useRuns } from "./RunsContext";
import { BASEMAP_LAYERS } from "components/mapSetup";
import { managedFetch } from "utils/FetchManager";
import useRun from "hooks/useRun";
import { useSimulations } from "contexts/SimulationsContext";
import { useNavigate } from "react-router-dom";
import usePage from "hooks/usePage";
import _uniq from "lodash/uniq";

const MapStateContext = createContext();

export const useMapState = () => useContext(MapStateContext);

const URBAN_IGNITION_REASON_COLORS = {
  entrypoint: "orange",
  direct: "red",
  spotting_structure: "darkcyan",
  spotting_ambient: "darkgreen",
  spotting_vegetation: "darkgreen",
  vegetation_to_structure: "orange",
  surface: "limegreen",
  default: "magenta",
};

const getIgnitionReasonColorExpression = (propertyName = "ignition_reason") => {
  return [
    "match",
    ["get", propertyName],
    "entrypoint",
    URBAN_IGNITION_REASON_COLORS.entrypoint,
    "direct",
    URBAN_IGNITION_REASON_COLORS.direct,
    "spotting_structure",
    URBAN_IGNITION_REASON_COLORS.spotting_structure,
    "spotting_ambient",
    URBAN_IGNITION_REASON_COLORS.spotting_ambient,
    "spotting_vegetation",
    URBAN_IGNITION_REASON_COLORS.spotting_vegetation,
    "vegetation_to_structure",
    URBAN_IGNITION_REASON_COLORS.vegetation_to_structure,
    "surface",
    URBAN_IGNITION_REASON_COLORS.surface,
    URBAN_IGNITION_REASON_COLORS.default,
  ];
};

function slopeToAccessibility(slope) {
  if (slope <= 10) {
    return "Accessible - Low Difficulty";
  } else if (slope <= 30) {
    return "Accessible - Moderate Difficulty";
  } else if (slope <= 55) {
    return "Accessible - High Difficulty";
  } else {
    return "Inaccessible -- Too Steep";
  }
}

async function loadOSMMask(bbox, AUTH_TOKEN, currentUser) {
  const resp = await managedFetch(
    `${API_URL}/osm_mask?bounds=${bbox.join(",")}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${AUTH_TOKEN}`,
        "Content-Type": "application/json",
      },
    },
    currentUser
  );
  const data = await resp.json();
  console.log("Loaded OSM mask:", data);
  return data;
}

// async function loadSpotMask(runID) {
//   const resp = await fetch(`${serviceURL}/ember_threat?run_prefix=${runID}`, {
//     method: "post",
//     headers: {
//       Authorization: `Bearer ${accessToken}`,
//       "Content-Type": "application/json",
//     },
//   });
//   const data = await resp.json();
//   console.log(data);
//   return data;
// }

export const MapStateProvider = ({ children }) => {
  const {
    intermixResults,
    intermixSymbolKey,
    intermixRunID,
    annotations,
    intermixScenarioTree,
    intermixScenarioSequence,
    intermixMitigations,
    intermixStructureHardening,
    intermixAdditionalBuildings,
    // intermixScenarioEmbers,
  } = useRuns();
  const { createSimulation, setUrbanSimToDuplicate } = useSimulations(); // TODO: remove this once we have a proper simulation context
  const { currRun } = useRun();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { projectId, runId, isSharePage } = usePage();

  const mapContainer = useRef(null);
  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [basemap, setBasemap] = useState("default");
  const [overlay, setOverlay] = useState("default");
  const [pathwaysVersion, setPathwaysVersion] = useState("baseline");
  const [allowedVersions, setAllowedVersions] = useState(["baseline"]);
  const [showEntryPoints, setShowEntryPoints] = useState(true);
  const [showOverrides, setShowOverrides] = useState(true);
  const [showIntermixMitigations, setShowIntermixMitigations] = useState(true);
  const [showIntermixStructureHardening, setShowIntermixStructureHardening] =
    useState(true);
  const [showIntermixAdditionalBuildings, setShowIntermixAdditionalBuildings] =
    useState(true);
  const [suitabilityLayer, setSuitabilityLayer] = useState("none");

  const AUTH_TOKEN = currentUser?.accessToken;

  const mapboxGeocoderControl = new MapboxGeocoder({
    accessToken: MAPBOX_TOKEN,
    mapboxgl: mapboxgl,
    placeholder: "Enter address or lat/lon",
  });

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    lng: 0,
    lat: 0,
  });
  const [annotationText, setAnnotationText] = useState("");

  const [basemapFeatureService, setBasemapFeatureService] = useState({});

  const popup = new mapboxgl.Popup({});

  // Iterating intermixResults.features on every symbol-key change used to
  // freeze the main thread for seconds on 500k-feature runs. Compute the
  // color-scale maxima once per FeatureCollection and reuse them across
  // re-renders.
  const intermixResultsMaxes = useMemo(() => {
    const features = intermixResults?.features;
    if (!features || features.length === 0) {
      return { maxIgnitionTime: 0, maxImportance: 0 };
    }
    let maxIgnitionTime = 0;
    let maxImportance = 0;
    for (let i = 0; i < features.length; i++) {
      const props = features[i].properties || {};
      const t = props.ignition_time;
      if (typeof t === "number" && t > maxIgnitionTime) maxIgnitionTime = t;
      const im = props.corrected_downstream_count;
      if (typeof im === "number" && im > maxImportance) maxImportance = im;
    }
    return { maxIgnitionTime, maxImportance };
  }, [intermixResults]);

  const intermixScenarioTreeMaxTime = useMemo(() => {
    const features = intermixScenarioTree?.features;
    if (!features || features.length === 0) return 0;
    let max = 0;
    for (let i = 0; i < features.length; i++) {
      const t = features[i].properties?.time;
      if (typeof t === "number" && t > max) max = t;
    }
    return max;
  }, [intermixScenarioTree]);

  // Handle basemap layer
  useEffect(() => {
    if (!map) {
      return;
    }

    const layers = Object.keys(BASEMAP_LAYERS);

    const addFeatureServiceLayer = (layer, layerDict) => {
      if (layer in basemapFeatureService) {
        return;
      }

      const fSId = layer + "-fs";
      const featureService = new FeatureService(fSId, map, {
        url: layerDict.url,
        minZoom: 9,
      });
      map.addLayer({
        id: layer,
        source: fSId,
        type: layerDict.style.type,
        paint: layerDict.style.paint,
        ...(layerDict.style.layout && { layout: layerDict.style.layout }),
        minzoom: 9,
      });

      if (layerDict.style.outline) {
        map.addLayer({
          id: layer + "-outline",
          source: fSId,
          type: "line",
          paint: layerDict.style.outline.paint,
          ...(layerDict.style.outline.layout && {
            layout: layerDict.style.outline.layout,
          }),
          minzoom: 9,
        });
      }

      if (layerDict.style.label) {
        map.addLayer({
          id: layer + "-labels",
          source: fSId,
          type: "symbol",
          layout: layerDict.style.label,
          paint: {
            "text-color": "#fff",
            "text-halo-color": "#000",
            "text-halo-width": 2,
          },
          minzoom: 9,
        });
      }

      const extendBasemapFeatureService = {
        ...basemapFeatureService,
        [layer]: featureService,
      };
      setBasemapFeatureService(extendBasemapFeatureService);
    };

    const removeFeatureServiceLayer = (layer) => {
      if (map.getLayer(layer)) {
        map.removeLayer(layer);
      }
      if (map.getLayer(layer + "-labels")) {
        map.removeLayer(layer + "-labels");
      }
      if (map.getLayer(layer + "-outline")) {
        map.removeLayer(layer + "-outline");
      }
      if (layer in basemapFeatureService) {
        if (map.getSource(layer + "-fs")) {
          basemapFeatureService[layer].destroySource();
        }
        const updatedBasemapFeatureService = { ...basemapFeatureService };
        delete updatedBasemapFeatureService[layer];
        setBasemapFeatureService(updatedBasemapFeatureService);
      }
    };

    const setLayerVisibility = (visibleLayer) => {
      layers.forEach((layer) => {
        const layerDict = BASEMAP_LAYERS[layer];
        if (layerDict.type === "featureService") {
          if (layer === visibleLayer) {
            addFeatureServiceLayer(layer, layerDict);
          } else {
            removeFeatureServiceLayer(layer);
          }
        } else if (layerDict.type === "osmMask") {
          // The OSM non-burnable layer lives under fixed source/layer ids
          // ("osm-nonburnable"), added once in addMapSourcesAndLayers.
          const visibility = layer === visibleLayer ? "visible" : "none";
          ["osm-nonburnable", "osm-nonburnable-outline"].forEach((id) => {
            if (map.getLayer(id)) {
              map.setLayoutProperty(id, "visibility", visibility);
            }
          });
        } else {
          map.setLayoutProperty(
            layer,
            "visibility",
            layer === visibleLayer ? "visible" : "none"
          );
        }
      });
    };

    if (basemap === "default") {
      setLayerVisibility(null);
    } else {
      setLayerVisibility(basemap);
    }
  }, [map, basemap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load OSM non-burnable features for the current viewport whenever that base
  // layer is active, refreshing as the user pans/zooms.
  useEffect(() => {
    if (!map || basemap !== "osm_nonburnable") {
      return;
    }

    let cancelled = false;

    const loadForViewport = async () => {
      if (!AUTH_TOKEN) {
        return;
      }
      const b = map.getBounds();
      const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
      try {
        const data = await loadOSMMask(bbox, AUTH_TOKEN, currentUser);
        if (!cancelled && data && map.getSource("osm-nonburnable")) {
          map.getSource("osm-nonburnable").setData(data);
        }
      } catch (err) {
        console.log("Error loading OSM non-burnables:", err);
      }
    };

    loadForViewport();
    map.on("moveend", loadForViewport);

    return () => {
      cancelled = true;
      map.off("moveend", loadForViewport);
    };
  }, [map, basemap, AUTH_TOKEN, currentUser]);

  // Left-click on a raster base layer -> popup with the ArcGIS identify value
  // at that point (LANDFIRE fuel model, disturbance, SDI).
  useEffect(() => {
    if (!map) {
      return;
    }

    const escapeHtml = (value) =>
      String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const identifyRaster = async (e) => {
      const config = BASEMAP_LAYERS[basemap];
      if (!config || !config.identifyUrl) {
        return;
      }
      const params = new URLSearchParams({
        geometry: JSON.stringify({
          x: e.lngLat.lng,
          y: e.lngLat.lat,
          spatialReference: { wkid: 4326 },
        }),
        geometryType: "esriGeometryPoint",
        sr: "4326",
        returnGeometry: "false",
        returnCatalogItems: "false",
        f: "json",
      });
      try {
        const resp = await fetch(`${config.identifyUrl}?${params.toString()}`);
        const data = await resp.json();
        const value = data?.value;
        const hasValue =
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== "NoData";
        const content = `
          <div class='prose popup'>
          <h6>${escapeHtml(config.name)}</h6>
          <hr />
          ${
            hasValue
              ? `<label>Value</label>: ${escapeHtml(value)}<br>`
              : "No data at this location.<br>"
          }
          </div>`;
        popup.setLngLat(e.lngLat).setHTML(content).addTo(map);
      } catch (err) {
        console.log("Error identifying raster layer:", err);
      }
    };

    map.on("click", identifyRaster);

    return () => {
      map.off("click", identifyRaster);
    };
  }, [map, basemap]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map | !intermixResults) {
      return;
    }

    if (!map.getSource("intermix-points")) {
      return;
    } else {
      if (intermixSymbolKey.startsWith("run_1_tree")) {
        map.setLayoutProperty("intermix-tree", "visibility", "visible");
        map.setLayoutProperty("intermix-tree-filter", "visibility", "visible");
        map.setLayoutProperty("intermix-points", "visibility", "none");
        map.setLayoutProperty("intermix-sequence", "visibility", "none");
        // map.setFilter('intermix-tree', ['>', ['get', 'time'], 200])
      } else if (intermixSymbolKey.startsWith("run_1_sequence")) {
        map.setLayoutProperty("intermix-tree", "visibility", "none");
        map.setLayoutProperty("intermix-tree-filter", "visibility", "none");
        map.setLayoutProperty("intermix-points", "visibility", "none");
        map.setLayoutProperty("intermix-sequence", "visibility", "visible");
      } else {
        let exp;
        if (intermixSymbolKey === "ignited") {
          exp = [
            "interpolate",
            ["linear"],
            ["get", "ignition_probability"],
            0,
            "#2b83ba",
            0.25,
            "#abdda4",
            0.5,
            "#ffffbf",
            0.75,
            "#fdae61",
            1,
            "#d7191c",
          ];
        } else if (intermixSymbolKey === "ignition_time") {
          const maxIgnitionTime = intermixResultsMaxes.maxIgnitionTime;
          exp = [
            "interpolate",
            ["linear"],
            ["get", "corrected_ignition_time"],
            0,
            "#ffffcc",
            maxIgnitionTime / 4,
            "#a1dab4",
            maxIgnitionTime / 2,
            "#41b6c4",
            (3 * maxIgnitionTime) / 4,
            "#2c7fb8",
            maxIgnitionTime,
            "#253494",
          ];
        } else if (intermixSymbolKey === "node_type") {
          exp = [
            "match",
            ["get", intermixSymbolKey],
            "entry",
            "#2b83ba",
            "vegetation",
            "#abdda4",
            "structure",
            "#fdae61",
            "#d3d3d3", // default color for unknown types
          ];
        } else if (intermixSymbolKey === "node_importance") {
          const maxImportance = intermixResultsMaxes.maxImportance;
          exp = [
            "interpolate",
            ["linear"],
            ["get", "corrected_downstream_count"],
            0,
            "#feedde",
            0.05 * maxImportance,
            "#fdbe85",
            0.1 * maxImportance,
            "#fd8d3c",
            0.25 * maxImportance,
            "#e6550d",
            maxImportance,
            "#a63603",
          ];
        } else if (intermixSymbolKey === "ignition_reason") {
          exp = getIgnitionReasonColorExpression();
        } else {
          throw new Error("Invalid intermixSymbolKey");
        }

        map.setPaintProperty("intermix-points", "circle-color", exp);
        map.setLayoutProperty("intermix-tree-filter", "visibility", "none");
        map.setLayoutProperty("intermix-tree", "visibility", "none");
        map.setLayoutProperty("intermix-sequence", "visibility", "none");
        map.setLayoutProperty("intermix-points", "visibility", "visible");
      }
    }
  }, [
    intermixSymbolKey,
    intermixResults,
    intermixResultsMaxes,
    map,
    intermixScenarioSequence,
  ]);

  useEffect(() => {
    if (!map || !intermixResults) {
      return;
    }
    if (map.getSource("intermix-points")) {
      map
        .getSource("intermix-points")
        .setData(
          intermixResults
            ? intermixResults
            : { type: "FeatureCollection", features: [] }
        );
    } else {
      map.addSource("intermix-points", {
        type: "geojson",
        data: intermixResults
          ? intermixResults
          : { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        source: "intermix-points",
        id: "intermix-points",
        type: "circle",
        paint: {
          "circle-opacity": 0.8,
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            1,
            14,
            3,
            18,
            6,
          ],
          "circle-stroke-width": 0.5,
          "circle-stroke-color": "rgba(255, 255, 255, 0.5)",
          "circle-stroke-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            0.5,
            15,
            0.9,
          ],
          "circle-color": [
            "interpolate",
            ["linear"],
            ["get", "ignition_probability"],
            0,
            "#2b83ba",
            0.25,
            "#abdda4",
            0.5,
            "#ffffbf",
            0.75,
            "#fdae61",
            1,
            "#d7191c",
          ],
        },
      });

      map.on("mouseenter", "intermix-points", (e) => {
        const coordinates = e.lngLat;
        const content = `
        <div class='prose popup'>
        <h6>Node ${e.features[0].properties.node}</h6>
        <hr />
        <label>Node Type</label>: ${e.features[0].properties.node_type}<br>
        <label>Ignition Reason</label>: ${e.features[0].properties.ignition_reason}<br>
        <label>Ignition Probability</label>: ${(e.features[0].properties.ignition_probability * 100).toFixed(1)}%<br>
        <label>Ignition Importance</label>: ${e.features[0].properties.corrected_downstream_count.toFixed(0)}<br>
        </div>`;
        popup.setLngLat(coordinates).setHTML(content).addTo(map);
      });
      map.on("mouseleave", "intermix-points", () => {
        map.getCanvas().style.cursor = "";
        popup.remove();
      });
    }

    const bounds = turf.bbox(intermixResults);
    try {
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { duration: 500, padding: 200 }
      );
    } catch (e) {
      console.error("Error fitting map bounds:", e);
    }
  }, [map, intermixResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Input geometries attached to the run. They are drawn independently of
  // `intermixResults` so a run that never produced results still shows what it
  // was asked to run with. Semi-transparent fills keep the result points
  // underneath readable, matching the `overrides` layer on the vegetation side.
  const intermixInputLayers = useMemo(
    () => [
      {
        id: "intermix-mitigations",
        data: intermixMitigations,
        color: "cyan",
      },
      {
        id: "intermix-structure-hardening",
        data: intermixStructureHardening,
        color: "#2ca25f",
      },
      {
        id: "intermix-additional-buildings",
        data: intermixAdditionalBuildings,
        color: "orange",
      },
    ],
    [
      intermixMitigations,
      intermixStructureHardening,
      intermixAdditionalBuildings,
    ]
  );

  useEffect(() => {
    if (!map) {
      return;
    }
    intermixInputLayers.forEach(({ id, data, color }) => {
      try {
        if (!data?.features?.length) {
          if (map.getLayer(id)) {
            map.removeLayer(id);
          }
          if (map.getSource(id)) {
            map.removeSource(id);
          }
          return;
        }
        if (map.getSource(id)) {
          map.getSource(id).setData(data);
        } else {
          map.addSource(id, { type: "geojson", data });
          map.addLayer({
            source: id,
            id,
            type: "fill",
            paint: {
              "fill-opacity": 0.5,
              "fill-color": color,
            },
          });
        }
      } catch (e) {
        console.error(`Error drawing ${id} layer:`, e);
      }
    });
  }, [map, intermixInputLayers]);

  // Visibility is separate from the data so toggling one layer does not push
  // its GeoJSON through the other two. Declared after the effect that adds the
  // layers, so a layer added in this commit is toggled in the same pass.
  useEffect(() => {
    if (!map) {
      return;
    }
    const visible = {
      "intermix-mitigations": showIntermixMitigations,
      "intermix-structure-hardening": showIntermixStructureHardening,
      "intermix-additional-buildings": showIntermixAdditionalBuildings,
    };
    intermixInputLayers.forEach(({ id }) => {
      if (!map.getLayer(id)) {
        return;
      }
      try {
        map.setLayoutProperty(
          id,
          "visibility",
          visible[id] ? "visible" : "none"
        );
      } catch (e) {
        console.error(`Error toggling ${id} layer:`, e);
      }
    });
  }, [
    map,
    intermixInputLayers,
    showIntermixMitigations,
    showIntermixStructureHardening,
    showIntermixAdditionalBuildings,
  ]);

  useEffect(() => {
    if (!map || !intermixScenarioSequence) {
      return;
    }
    if (map.getSource("intermix-sequence")) {
      map.getSource("intermix-sequence").setData(intermixScenarioSequence);
    } else {
      map.addSource("intermix-sequence", {
        type: "geojson",
        data: intermixScenarioSequence
          ? intermixScenarioSequence
          : { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        source: "intermix-sequence",
        id: "intermix-sequence",
        type: "circle",
        paint: {
          "circle-opacity": 0.8,
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            1,
            14,
            3,
            18,
            6,
          ],
          "circle-stroke-width": 0.5,
          "circle-stroke-color": "rgba(255, 255, 255, 0.5)",
          "circle-stroke-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            0.5,
            15,
            0.9,
          ],
          "circle-color": getIgnitionReasonColorExpression("ignition_reason"),
        },
      });
      map.setLayoutProperty("intermix-sequence", "visibility", "none");
      // map.on("mouseenter", "intermix-sequence", (e) => {
      //   const coordinates = e.lngLat;
      //   const content = `
      //   <div class='prose popup'>
      //   <h6>Node ${e.features[0].properties.node}</h6>
      //   <hr />
      //   <label>Ignition Time</label>: ${e.features[0].properties.ignition_time.toFixed(2)} minutes<br>
      //   <label>Ignition Probability</label>: ${(e.features[0].properties.ignition_probability * 100).toFixed(1)}%<br>
      //   <label>Ignition Importance</label>: ${e.features[0].properties.corrected_downstream_count.toFixed(0)}<br>
      //   </div>`;
      //   popup.setLngLat(coordinates).setHTML(content).addTo(map);
      // });
      // map.on("mouseleave", "intermix-sequence", () => {
      //   map.getCanvas().style.cursor = "";
      //   popup.remove();
      // });
      map.on("mouseenter", "intermix-sequence", (e) => {
        console.log(e.features);
      });
    }
  }, [map, intermixScenarioSequence]);

  useEffect(() => {
    if (!map || !intermixScenarioTree) {
      return;
    }
    if (map.getSource("intermix-tree")) {
      map.getSource("intermix-tree").setData(intermixScenarioTree);
      // map.getSource("intermix-tree-filter").setData(intermixScenarioTree);
    } else {
      map.addSource("intermix-tree", {
        type: "geojson",
        data: intermixScenarioTree
          ? intermixScenarioTree
          : { type: "FeatureCollection", features: [] },
      });
      const maxIgnitionTime = intermixScenarioTreeMaxTime;

      map.addLayer({
        source: "intermix-tree",
        id: "intermix-tree",
        type: "line",
        paint: {
          "line-opacity": 0.1,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            1,
            14,
            2,
            18,
            3,
          ],
          "line-color": [
            "interpolate",
            ["linear"],
            ["get", "time"],
            0,
            "#feebe2",
            maxIgnitionTime * 0.25,
            "#fbb4b9",
            maxIgnitionTime * 0.5,
            "#f768a1",
            maxIgnitionTime * 0.75,
            "#c51b7d",
            maxIgnitionTime,
            "#7a0177",
          ],
        },
        layout: {
          visibility: "none",
        },
      });
      map.addLayer({
        source: "intermix-tree",
        id: "intermix-tree-filter",
        type: "line",
        paint: {
          "line-opacity": 1,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            1,
            14,
            2,
            18,
            4,
          ],
          "line-color": "orange",
        },
        layout: {
          visibility: "none",
        },
      });

      map.on("mouseenter", "intermix-tree-filter", (e) => {
        console.log(e.features);
      });
    }
  }, [intermixScenarioTree, intermixScenarioTreeMaxTime, map]);

  const clearMapLayers = useCallback(() => {
    if (!map) {
      return;
    }
    const layers = [
      "pathways",
      "suitability",
      "ignition",
      "overrides",
      "entryPoints",
      "intermix-tree",
      "intermix-tree-filter",
      "intermix-sequence",
      "intermix-points",
      "intermix-mitigations",
      "intermix-structure-hardening",
      "intermix-additional-buildings",
    ];
    layers.forEach((layer) => {
      if (map.getLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    layers.forEach((layer) => {
      if (map.getSource(layer)) {
        map.removeSource(layer);
      }
    });
  }, [map]);

  // `currRun` is for vegetation simulations only
  useEffect(() => {
    if (!runId) {
      clearMapLayers();
    }
  }, [clearMapLayers, runId]);

  // Show current run on map
  useEffect(() => {
    if (!window.turf) {
      return;
    }

    if (!currRun || !map) {
      return;
    }

    try {
      const layers = [
        "pathways",
        "suitability",
        "ignition",
        "overrides",
        "entryPoints",
      ];
      layers.forEach((layer) => {
        if (map.getLayer(layer)) {
          map.removeLayer(layer);
        }
        if (map.getSource(layer)) {
          map.removeSource(layer);
        }
      });
    } catch (e) {
      console.error("Error setting map layout properties:", e);
    }

    const bounds = turf.bbox(
      currRun.pathways?.features?.length > 0
        ? currRun.pathways
        : currRun.ignition
    );

    try {
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { duration: 500, padding: 200 }
      );
    } catch (e) {
      console.error("Error fitting map bounds:", e);
    }

    if (map.getSource("entryPoints")) {
      map
        .getSource("entryPoints")
        .setData(
          currRun.entry_points
            ? currRun.entry_points
            : { type: "FeatureCollection", features: [] }
        );
    } else {
      map.addSource("entryPoints", {
        type: "geojson",
        data: currRun.entry_points
          ? currRun.entry_points
          : { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        source: "entryPoints",
        id: "entryPoints",
        type: "fill",
        paint: {
          "fill-opacity": 0.4,
          "fill-color": "orange",
        },
      });
    }

    if (map.getSource("pathways")) {
      map.getSource("pathways").setData(currRun.pathways);
    } else {
      map.addSource("pathways", {
        type: "geojson",
        data: currRun.pathways,
      });
      map.addLayer({
        source: "pathways",
        id: "pathways",
        type: "line",
        paint: {
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            1,
            10,
            2,
            15,
            4,
          ],
          "line-color": [
            "interpolate",
            ["linear"],
            ["get", "arrival"],
            0,
            "yellow",
            720,
            "orange",
            1440,
            "red",
            2160,
            "purple",
            2880,
            "blue",
          ],
        },
      });
    }
    if (map.getSource("overrides")) {
      map
        .getSource("overrides")
        .setData(
          currRun.overrides
            ? currRun.overrides
            : { type: "FeatureCollection", features: [] }
        );
    } else {
      map.addSource("overrides", {
        type: "geojson",
        data: currRun.overrides
          ? currRun.overrides
          : { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        source: "overrides",
        id: "overrides",
        type: "fill",
        paint: {
          "fill-opacity": 0.5,
          "fill-color": "cyan",
        },
      });
    }

    if (map.getSource("ignition")) {
      map.getSource("ignition").setData(currRun.ignition);
    } else {
      map.addSource("ignition", {
        type: "geojson",
        data: currRun.ignition,
      });
      map.addLayer(
        {
          source: "ignition",
          id: "ignition",
          type: "line",
          paint: {
            "line-color": "red",
            "line-width": 3,
          },
        },
        "waterway-label"
      );
    }

    if (map.getSource("suitability")) {
      map.getSource("suitability").setData(currRun.suitability);
    } else {
      map.addSource("suitability", {
        type: "geojson",
        data: currRun.suitability,
      });

      map.addLayer({
        source: "suitability",
        id: "suitability",
        type: "fill",
        paint: {
          "fill-opacity": 0.5,
          "fill-color":
            suitabilityLayer === "benefit_to_cost_ratio_community"
              ? [
                  "interpolate",
                  ["linear"],
                  ["to-number", ["get", "benefit_to_cost_ratio_community"]],
                  0,
                  "#440154",
                  0.5,
                  "#3b528b",
                  1,
                  "#21918c",
                  2,
                  "#5ec962",
                  3,
                  "#fde725",
                  6,
                  "#fde725",
                ]
              : [
                  "interpolate",
                  ["linear"],
                  ["to-number", ["get", suitabilityLayer]],
                  0,
                  "#440154",
                  20,
                  "#3b528b",
                  40,
                  "#21918c",
                  60,
                  "#5ec962",
                  80,
                  "#fde725",
                  100,
                  "#fde725",
                ],
        },
      });
      map.on("click", "suitability", (e) => {
        const coordinates = e.lngLat;
        console.log(e.features[0].properties.slope);

        const content = `
        <div class='prose popup'>
        <h6>Community-Weighted CBR: ${e.features[0].properties.benefit_to_cost_ratio_community.toFixed(2)}</h6>
        <hr />
        <label>Total Benefit</label>: ${e.features[0].properties.total_benefit_community ? e.features[0].properties.total_benefit_community : 0}<br>
        <label>Total Cost</label>: ${e.features[0].properties.total_cost ? e.features[0].properties.total_cost : 0}<br>
        <label>Slope Cost</label>: ${e.features[0].properties.slope_cost ? e.features[0].properties.slope_cost : 0}<br>
        <label>Access Cost</label>: ${e.features[0].properties.road_cost ? e.features[0].properties.road_cost : 0}<br>
        <label>Rate of Spread Benefit</label>: ${e.features[0].properties.pre_ros_benefit ? e.features[0].properties.pre_ros_benefit : 0}<br>
        <label>Mitigation Benefit</label>: ${e.features[0].properties.delta_benefit ? e.features[0].properties.delta_benefit : 0}<br>
        <label>Community Density Benefit</label>: ${e.features[0].properties.building_benefit ? e.features[0].properties.building_benefit : 0}<br>
        <label>Network Benefit</label>: ${e.features[0].properties.density_benefit ? e.features[0].properties.density_benefit : 0}<br>
        <label>Slope Accessibility</label>: ${slopeToAccessibility(e.features[0].properties.slope)}<br>
        <label>Average Slope</label>: ${e.features[0].properties.slope}°<br>
        <div>
        `;
        popup.setLngLat(coordinates).setHTML(content).addTo(map);
      });
    }
  }, [map, currRun, suitabilityLayer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle on and off the entry points layer, when currRun changes
  useEffect(() => {
    if (!map) {
      return;
    }
    if (!map.getLayer("entryPoints")) {
      return;
    }
    if (showEntryPoints) {
      map.setLayoutProperty("entryPoints", "visibility", "visible");
    } else {
      map.setLayoutProperty("entryPoints", "visibility", "none");
    }
  }, [map, showEntryPoints, currRun]);

  // Toggle on and off the overrides layer
  useEffect(() => {
    if (!map) {
      return;
    }
    if (!map.getLayer("overrides")) {
      return;
    }
    if (showOverrides) {
      map.setLayoutProperty("overrides", "visibility", "visible");
    } else {
      map.setLayoutProperty("overrides", "visibility", "none");
    }
  }, [map, showOverrides, currRun]);

  // Handle overlay
  useEffect(() => {
    if (!map || !currRun) {
      return;
    }
    (async () => {
      if (overlay === "osm_mask") {
        try {
          const box = turf.bbox(currRun.pathways);
          box[0] = box[0] - 0.03;
          box[1] = box[1] - 0.03;
          box[2] = box[2] + 0.03;
          box[3] = box[3] + 0.03;
          const maskData = await loadOSMMask(box, AUTH_TOKEN, currentUser);

          // Check if we have data and map is still valid (might have been removed during logout)
          if (maskData && map && map.getSource("overlay-vector")) {
            map.getSource("overlay-vector").setData(maskData);
            map.setLayoutProperty("overlay-vector", "visibility", "visible");
          }
        } catch (err) {
          // Handle any errors, including aborted requests
          console.log("Error loading OSM mask:", err);
        }
      } else {
        var targetURL = currRun[overlay];
        if (overlay.includes("hypothetical")) {
          targetURL = currRun.hypothetical_files[overlay];
          console.log("Hypothetical overlay URL:", targetURL);
        }

        if (targetURL) {
          const tt = targetURL.replace('"', "");
          let max = 1;
          let min = 0;
          let cmap = "autumn_r";
          if (overlay === "wind_speed") {
            max = 25; // mph
            cmap = "viridis";
          } else if (overlay === "wind_direction") {
            max = 360;
            cmap = "hsv";
          } else if (overlay.includes("flame_length")) {
            max = 2;
            cmap = "cool";
          } else if (overlay.includes("arrival_time")) {
            max = currRun["config"]["simulation"]["duration"]; //minutes
            cmap = "viridis";
          } else if (overlay.includes("rate_of_spread")) {
            max = 25;
            cmap = "viridis";
          } else if (overlay === "arrival_delta") {
            max = 360;
            min = -360;
            cmap = "cool";
          } else if (overlay === "osm_mask") {
            max = 1;
            cmap = "coolwarm";
          }
          // &bidx=1&rescale=0,${max}&colormap_name=${cmap}
          const baseUrl = `https://svc-raster-tiles-b3i5nh3t5a-uw.a.run.app/cog/tiles/WebMercatorQuad/{z}/{x}/{y}@2x?url=${tt}&bidx=1&rescale=${min},${max}&colormap_name=${cmap}`;
          console.log(baseUrl);
          map.setLayoutProperty("overlay", "visibility", "visible");
          map.getSource("overlay").setTiles([baseUrl]).reload();
        } else {
          map.setLayoutProperty("overlay", "visibility", "none");
          map.setLayoutProperty("overlay-vector", "visibility", "none");
        }
      }
    })();
  }, [map, overlay, currRun, AUTH_TOKEN, currentUser]);

  // handle switching pathways version
  useEffect(() => {
    if (!map || !currRun) {
      return;
    }
    if (pathwaysVersion === "baseline") {
      map.setFilter("pathways", ["==", "run_type", "baseline"]);
    } else if (pathwaysVersion === "override") {
      map.setFilter("pathways", ["==", "run_type", "override"]);
    } else {
      map.setFilter("pathways", null);
    }
  }, [map, pathwaysVersion, currRun]);

  // handle allowed versions
  useEffect(() => {
    if (!currRun) {
      return;
    }
    const runTypes = currRun.pathways.features.map(
      (feature) => feature.properties.run_type
    );
    const allowedVersions = _uniq(runTypes);
    setAllowedVersions(allowedVersions);
    if (allowedVersions.includes("override")) {
      setPathwaysVersion("override");
    } else {
      setPathwaysVersion("baseline");
    }
  }, [currRun]);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (!map.getLayer("suitability")) {
      return;
    }
    if (!suitabilityLayer || suitabilityLayer === "none") {
      map.setLayoutProperty("suitability", "visibility", "none");
      return;
    } else {
      map.setLayoutProperty("suitability", "visibility", "visible");
    }

    let suitabilityExp;
    if (suitabilityLayer === "benefit_to_cost_ratio_community") {
      suitabilityExp = [
        "interpolate",
        ["linear"],
        ["to-number", ["get", "benefit_to_cost_ratio_community"]],
        0,
        "#440154",
        0.5,
        "#3b528b",
        1,
        "#21918c",
        2,
        "#5ec962",
        3,
        "#fde725",
        6,
        "#fde725",
      ];
    } else if (suitabilityLayer === "total_benefit_community") {
      suitabilityExp = [
        "interpolate",
        ["linear"],
        ["to-number", ["get", "total_benefit_community"]],
        0,
        "#440154",
        50,
        "#3b528b",
        100,
        "#21918c",
        150,
        "#5ec962",
        200,
        "#fde725",
        250,
        "#fde725",
      ];
    } else if (suitabilityLayer === "slope_accessibility") {
      suitabilityExp = [
        "case",
        ["<", ["to-number", ["get", "slope"]], 10],
        "#2b83ba",
        ["<", ["to-number", ["get", "slope"]], 30],
        "#abdda4",
        ["<", ["to-number", ["get", "slope"]], 55],
        "#fdae61",
        "#5e3c99",
      ];
    } else {
      suitabilityExp = [
        "interpolate",
        ["linear"],
        ["to-number", ["get", suitabilityLayer]],
        0,
        "#440154",
        20,
        "#3b528b",
        40,
        "#21918c",
        60,
        "#5ec962",
        80,
        "#fde725",
        100,
        "#fde725",
      ];
    }

    map.setPaintProperty("suitability", "fill-color", suitabilityExp);
  }, [map, suitabilityLayer, currRun]); // update suitability layer when currRun changes

  useEffect(() => {
    if (!map) {
      return;
    }
    map.on("contextmenu", handleRightClick);
    map.on("click", handleCloseContextMenu);

    return () => {
      map.off("contextmenu", handleRightClick);
      map.off("click", handleCloseContextMenu);
    };
  }, [map]);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (!annotations) {
      return;
    }

    if (map.getLayer("annotations")) {
      map.getSource("annotations").setData(annotations);
    } else {
      map.addSource("annotations", {
        type: "geojson",
        data: annotations,
      });
      map.addLayer({
        id: "annotations",
        type: "symbol",
        source: "annotations",
        layout: {
          "icon-image": "border-dot-13",
          "icon-size": 2,
        },
      });
    }
    map.on("mouseenter", "annotations", (e) => {
      map.getCanvas().style.cursor = "pointer";
      const coordinates = e.lngLat;
      const content = `
        <div class='prose popup'>
        <h6>${e.features[0].properties.user}</h6>
        <p>${new Date(e.features[0].properties.created_at).toLocaleDateString()}</p>
        <hr />
        <label>Note</label>: ${e.features[0].properties.note}<br>
        </div>`;
      popup.setLngLat(coordinates).setHTML(content).addTo(map);
    });
    map.on("mouseleave", "annotations", () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    });
  }, [map, annotations]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitAnnotation = async (annotation) => {
    console.log("Submitting annotation:", annotation);
    annotation.user = currentUser.email;
    const response = await managedFetch(
      `${API_URL}/note/${+annotation.simulation_id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${AUTH_TOKEN}`,
        },
        body: JSON.stringify(annotation),
      },
      currentUser
    );
    const data = await response.json();
    console.log("Response:", data);

    if (!response.ok) {
      console.error("Failed to submit annotation");
    } else {
      console.log("Annotation submitted successfully");
    }
  };

  const handleRightClick = (e) => {
    console.log("Right click event:", e);
    e.preventDefault();
    const lngLat = e.lngLat;
    const x = e.point.x;
    const y = e.point.y;
    setContextMenu({ visible: true, x, y, lng: lngLat.lng, lat: lngLat.lat });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, lng: 0, lat: 0 });
  };

  return (
    <MapStateContext.Provider
      value={{
        map,
        setMap,
        mapLoaded,
        setMapLoaded,
        mapContainer,
        mapboxGeocoderControl,

        basemap,
        setBasemap,
        overlay,
        setOverlay,

        pathwaysVersion,
        setPathwaysVersion,

        allowedVersions,
        setAllowedVersions,

        suitabilityLayer,
        setSuitabilityLayer,

        showEntryPoints,
        setShowEntryPoints,
        showOverrides,
        setShowOverrides,
        showIntermixMitigations,
        setShowIntermixMitigations,
        showIntermixStructureHardening,
        setShowIntermixStructureHardening,
        showIntermixAdditionalBuildings,
        setShowIntermixAdditionalBuildings,
      }}
    >
      {children}
      {contextMenu.visible && !isSharePage && (
        <div
          style={{
            position: "absolute",
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: "white",
            border: "1px solid #ccc",
            zIndex: 1000,
            width: "300px",
          }}
        >
          <ul>
            {intermixRunID && (
              <li>
                <Button
                  onClick={() => {
                    submitAnnotation({
                      longitude: contextMenu.lng,
                      latitude: contextMenu.lat,
                      note: annotationText,
                      simulation_id: Number(intermixRunID),
                    });
                    setAnnotationText("");
                    handleCloseContextMenu();
                  }}
                  variant="contained"
                  color="primary"
                  sx={{ width: "100%" }}
                >
                  Add Annotation
                </Button>
                <div>
                  <textarea
                    placeholder="Add your annotation here"
                    onChange={(e) => setAnnotationText(e.target.value)}
                    value={annotationText}
                    style={{
                      width: "100%",
                      height: "100px",
                      resize: "none",
                      marginTop: "10px",
                    }}
                  ></textarea>
                </div>
              </li>
            )}
            <li>
              <Button
                onClick={() => {
                  navigate(`/project/${projectId}/urban`);
                  setUrbanSimToDuplicate({
                    longitude: contextMenu.lng,
                    latitude: contextMenu.lat,
                  });
                  createSimulation("urban");
                  handleCloseContextMenu();
                }}
                variant="contained"
                color="primary"
                sx={{ width: "100%" }}
              >
                Create Intermix Simulation
              </Button>
            </li>
          </ul>
        </div>
      )}
    </MapStateContext.Provider>
  );
};
